import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { SEO } from "../components/common/SEO";
import { CmsIcon } from "../components/common/CmsIcon";
import { useLanguage } from "../context/LanguageProvider";
import { useSiteData } from "../context/SiteDataProvider";
import { pick, UI_STRINGS } from "../lib/i18n";
import { domainOptionValue } from "../lib/format";
import { ArrowRight, Dot } from "lucide-react";

export default function Practice() {
    const { lang } = useLanguage();
    const { practiceAreas, siteContent, ready } = useSiteData();
    const seo = siteContent?.seo?.practice || {};
    const { hash } = useLocation();

    // Re-scroll when practice areas render after data loads
    useEffect(() => {
        if (!ready || !hash) return;
        const el = document.getElementById(hash.slice(1));
        if (el) {
            requestAnimationFrame(() =>
                el.scrollIntoView({ behavior: "smooth", block: "start" }),
            );
        }
    }, [ready, hash, practiceAreas.length]);

    return (
        <div data-testid="page-practice" className="bg-ink">
            <SEO
                title={pick(seo.title, lang) || "Domaines de pratique"}
                description={pick(seo.description, lang)}
                lang={lang}
            />

            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16 md:py-20">
                <div className="max-w-3xl">
                    <div className="eyebrow mb-3">
                        {pick(UI_STRINGS.nav.practice, lang)}
                    </div>
                    <h1 className="font-serif text-4xl md:text-5xl leading-tight text-neutral-50">
                        {pick(
                            {
                                fr: "Sept domaines, une même exigence.",
                                en: "Seven areas, one standard.",
                            },
                            lang,
                        )}
                    </h1>
                    <p className="mt-6 text-neutral-300 text-lg leading-relaxed">
                        {pick(
                            {
                                fr: "Le cabinet intervient en droit civil, pénal, des affaires (OHADA), du travail, foncier, en conseil et rédaction d'actes, ainsi que sur les dossiers transfrontaliers Burkina–Canada — spécificité rare du cabinet.",
                                en: "The firm practises civil, criminal, business (OHADA), labour and land law, provides legal counsel and drafting, and handles cross-border Burkina–Canada matters — a rare specificity of the firm.",
                            },
                            lang,
                        )}
                    </p>
                </div>

                <nav
                    aria-label="Practice areas quick nav"
                    className="mt-10 flex flex-wrap gap-2"
                    data-testid="practice-quick-nav"
                >
                    {practiceAreas.map((pa) => (
                        <a
                            key={pa.slug}
                            href={`#${pa.slug}`}
                            data-testid={`practice-nav-${pa.slug}`}
                            className="text-xs px-3 py-2 border border-ink-border hover:border-gold hover:text-gold text-neutral-300 transition-colors tracking-wide"
                        >
                            {pick(pa.title, lang)}
                        </a>
                    ))}
                </nav>
            </section>

            <div className="border-t border-ink-border">
                {practiceAreas.map((pa, idx) => (
                    <section
                        key={pa.slug}
                        id={pa.slug}
                        data-testid={`practice-section-${pa.slug}`}
                        className={`scroll-mt-24 py-16 md:py-20 border-b border-ink-border ${
                            idx % 2 === 0 ? "bg-ink" : "bg-ink-deep"
                        }`}
                    >
                        <div className="mx-auto max-w-7xl px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                            <div className="lg:col-span-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 flex items-center justify-center border border-gold/60 bg-ink">
                                        <CmsIcon name={pa.icon} size={26} />
                                    </div>
                                    <div className="eyebrow text-neutral-400">
                                        {String(pa.order).padStart(2, "0")} /
                                        07
                                    </div>
                                </div>
                                <h2 className="font-serif text-3xl md:text-4xl text-neutral-50 mt-6 leading-tight">
                                    {pick(pa.title, lang)}
                                </h2>
                                <Link
                                    to={`/contact?domain=${encodeURIComponent(domainOptionValue(pa.slug))}#form`}
                                    className="btn-gold mt-8"
                                    data-testid={`practice-cta-${pa.slug}`}
                                >
                                    {pick(UI_STRINGS.cta.consult_firm, lang)}
                                    <ArrowRight size={16} />
                                </Link>
                            </div>
                            <div className="lg:col-span-8">
                                <p className="text-neutral-300 leading-relaxed text-base md:text-lg">
                                    {pick(pa.description, lang)}
                                </p>
                                <div className="mt-8">
                                    <div className="eyebrow mb-3">
                                        {pick(
                                            UI_STRINGS.sections.situations_types,
                                            lang,
                                        )}
                                    </div>
                                    <ul className="space-y-2">
                                        {(pa.example_situations?.[lang] || [])
                                            .map((s, i) => (
                                                <li
                                                    key={i}
                                                    className="flex items-start gap-2 text-sm text-neutral-300"
                                                >
                                                    <Dot
                                                        size={22}
                                                        className="text-gold shrink-0 -ml-2"
                                                    />
                                                    <span>{s}</span>
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
}
